import type { CardType } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { PartnerCardType } from 'apis/partnerIntegrations';
import { usePartnerWidgetFeatureFlags } from 'utils/partnerIntegrationsWidget/usePartnerWidgetFeatureFlags';
import { useCurrentUser, userHasRole } from 'utils/user';

import { Integration, integrations } from './integrations';

type UseIntegrationsResponse = Record<PartnerCardType | CardType, Integration>;

/**
 * A hook that returns a list of integrations that the current user has access
 * to.
 */
export const useIntegrations = (): UseIntegrationsResponse => {
  const { user } = useCurrentUser();
  const partnerWidgetFeatureFlags = usePartnerWidgetFeatureFlags();

  return useMemo(
    () =>
      Object.keys(integrations)
        .filter((key) => userHasRole(user, integrations[key].minimumRole))
        .filter((key) =>
          integrations[key].integrationType === 'partnerWidget'
            ? partnerWidgetFeatureFlags[key]
            : true,
        )
        .sort((a, b) => a.localeCompare(b))
        .reduce((obj, key) => {
          return {
            ...obj,
            [key]: integrations[key],
          };
        }, {} as UseIntegrationsResponse),
    [user, partnerWidgetFeatureFlags],
  );
};
